/**
 * AntdThemeBridge — makes antd follow the app's light/dark mode.
 *
 * The ConfigProvider in index.js passed a font and a primary colour and no
 * `algorithm`, so antd used its default (light) one and every antd surface
 * stayed light regardless of `data-theme`: white Modal bodies, white Select and
 * Input fills, near-black label text on the dark page. Same defect the MUI
 * theme had, one line below it.
 *
 * Two halves to the fix:
 *
 *   1. `theme.darkAlgorithm` when the resolved theme is dark, with the seed
 *      tokens pointed at the trodo palette so antd's surfaces line up with the
 *      rest of the app instead of with antd's own greys.
 *
 *   2. `<App component={false}>`. antd v5's STATIC `message.success(...)` /
 *      `notification` / `Modal.confirm` calls render through a detached root
 *      that never sees ConfigProvider context, so they would keep painting
 *      light toasts however the algorithm is set — a documented v5 limitation,
 *      not something the theme can reach. `App` publishes context-aware
 *      replacements that `App.useApp()` hands to components. `component={false}`
 *      means it contributes context and no DOM node, so it cannot disturb the
 *      app's layout.
 *
 * Values are real colours, not `var(--token)`: antd derives whole palettes from
 * the seeds (@ant-design/colors) and needs to parse them, exactly like MUI.
 */
import { App as AntdApp, ConfigProvider, theme as antdTheme } from 'antd';
import React, { useMemo } from 'react';
import { useTheme } from 'shared/theme/ThemeContext';
import { palette as tokens, PRIMARY_HEX } from 'shared/theme/trodoColors';

/**
 * Build the antd theme config for one mode.
 *
 * Exported for tests — `buildAntdTheme('dark').algorithm` should be antd's dark
 * algorithm, and the surfaces should be trodo's rather than antd's.
 *
 * @param {'light'|'dark'} mode
 */
export function buildAntdTheme(mode) {
  const pick = (token) => token[mode];

  return {
    algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      fontFamily: "'Inter', sans-serif",
      colorPrimary: PRIMARY_HEX,
      // Matches the single radius the rest of the app collapsed to in
      // tailwind.config.js; antd's default is 6 for most things but 8 for
      // Modal/Card, which read rounder than the panels around them.
      borderRadius: 6,

      // Surfaces. `colorBgElevated` is what Modal, Select dropdowns, Tooltip
      // and Popover render on — the same role --surface-raised exists for.
      colorBgBase: pick(tokens.surface.base),
      colorBgContainer: pick(tokens.surface.base),
      colorBgElevated: pick(tokens.surface.raised),
      colorBgLayout: pick(tokens.surface.sunken),
      colorBgMask: pick(tokens.surface.overlay),

      // Text. antd's own scale is alpha-on-background; these are the solid
      // tokens the rest of the app reads, so labels match their neighbours.
      colorTextBase: pick(tokens.text.primary),
      colorText: pick(tokens.text.primary),
      colorTextSecondary: pick(tokens.text.secondary),
      colorTextTertiary: pick(tokens.text.muted),
      colorTextQuaternary: pick(tokens.text.disabled),

      colorBorder: pick(tokens.border.default),
      colorBorderSecondary: pick(tokens.border.subtle),

      colorError: pick(tokens.feedback.error),
      colorWarning: pick(tokens.feedback.warning),
      colorSuccess: pick(tokens.feedback.success),
      colorInfo: pick(tokens.feedback.info),
    },
  };
}

/**
 * Provides antd's theme and its context-aware message/notification/modal APIs.
 * Must render INSIDE `ThemeProvider` from shared/theme/ThemeContext.
 */
export default function AntdThemeBridge({ children }) {
  const { theme } = useTheme();
  const config = useMemo(
    () => buildAntdTheme(theme === 'dark' ? 'dark' : 'light'),
    [theme],
  );

  return (
    <ConfigProvider theme={config}>
      {/* component={false}: context only, no wrapper element. */}
      <AntdApp component={false}>{children}</AntdApp>
    </ConfigProvider>
  );
}
